import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import SharedLightbox from '../components/SharedLightbox';
import './SharedGalleryPage.css';

const PAGE_SIZE = 30;

export default function SharedGalleryPage() {
  const { token } = useParams();

  const [linkMeta, setLinkMeta] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const sentinelRef = useRef(null);

  // 1. Validate the share token first
  useEffect(() => {
    api.get(`/api/share/view/${token}`)
      .then(({ data }) => setLinkMeta(data))
      .catch(() => setLinkError('This share link is invalid or has expired.'));
  }, [token]);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    try {
      const { data } = await api.get(`/api/share/view/${token}/media?page=${pageNum}&limit=${PAGE_SIZE}`);
      setItems((prev) => append ? [...prev, ...data.items] : data.items);
      setTotalPages(data.pagination.totalPages);
    } catch {
      setLinkError('Failed to load media.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token]);

  // 2. Load media once token is validated
  useEffect(() => {
    if (!linkMeta) return;
    setLoading(true);
    fetchPage(1);
  }, [linkMeta, fetchPage]);

  // 3. Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingMore && page < totalPages) {
        const next = page + 1;
        setPage(next);
        setLoadingMore(true);
        fetchPage(next, true);
      }
    }, { rootMargin: '400px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fetchPage, loadingMore, page, totalPages]);

  // ── Render: invalid link ──
  if (linkError) {
    return (
      <div className="sg-error">
        <span className="sg-error__icon">🔗</span>
        <h2>Link unavailable</h2>
        <p>{linkError}</p>
        <Link to="/login" className="btn btn-primary">Sign in to your account</Link>
      </div>
    );
  }

  // ── Render: validating ──
  if (!linkMeta) {
    return <div className="sg-loading"><div className="spinner" /></div>;
  }

  const brandName = linkMeta.ownerName || 'Mediaxone';

  return (
    <div className="sg-page">

      {/* Header — brand shows owner's name */}
      <header className="sg-header">
        <div className="sg-brand">
          <span className="brand-icon">⬡</span>
          <span>{brandName}</span>
        </div>
        <div className="sg-header-meta">
          <span className="sg-title">{linkMeta.event?.name || linkMeta.label || 'Shared gallery'}</span>
          <span className="sg-readonly-badge">View only</span>
        </div>
      </header>

      {/* Gallery body */}
      <div className="sg-body">
        {loading ? (
          <div className="sg-loading"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="sg-empty">
            <span>☁</span>
            <p>Nothing has been shared yet.</p>
          </div>
        ) : (
          <>
            <div className="sg-grid">
              {items.map((item) => (
                <SharedMediaCard
                  key={item.id}
                  item={item}
                  onClick={() => setSelectedId(item.id)}
                />
              ))}
            </div>
            <div ref={sentinelRef} className="sg-sentinel">
              {loadingMore && <div className="spinner" />}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="sg-footer">
        <span>
          Mediaxone Platform designed and developed by{' '}
          <a
            href="https://cloudxier.com"
            target="_blank"
            rel="noopener noreferrer"
            className="sg-footer-link"
          >
            Cloudxier
          </a>
        </span>
      </footer>

      {selectedId && (
        <SharedLightbox
          mediaId={selectedId}
          token={token}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ── Inline card component (no delete button) ──
function SharedMediaCard({ item, onClick }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { rootMargin: '300px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isVideo = item.fileType?.startsWith('video');

  return (
    <div
      ref={ref}
      className="sg-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {visible && (
        item.thumbnailUrl
          ? <img
              className="sg-card__thumb"
              src={item.thumbnailUrl}
              alt={item.originalName}
              loading="lazy"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
          : <div className="sg-card__placeholder">{isVideo ? '🎬' : '🖼'}</div>
      )}
      {isVideo && <span className="sg-card__badge">▶</span>}
    </div>
  );
}
